import React, { useState, useCallback } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { requestWithAuthorization } from "../../network/apiRequests";
import { auth } from "../../firebase";

interface ExportDialogProps {
  show: boolean;
  onHide: () => void;
  userId?: string;
}


export default function ExportDialog({ show, onHide, userId }: ExportDialogProps) {
  const [startDateTime, setStartDateTime] = useState<string>("");
  const [endDateTime, setEndDateTime] = useState<string>("");
  const [sensorId, setSensorId] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState<{ raw: boolean; processed: boolean }>({
    raw: false,
    processed: false
  });
  const [error, setError] = useState<string>("");
  const [exportFormat, setExportFormat] = useState<"csv" | "json">("csv");


  const validateForm = useCallback(() => {
    if (!startDateTime) {
      setError("Start date-time is required");
      return false;
    }
    if (!endDateTime) {
      setError("End date-time is required");
      return false;
    }
    if (new Date(startDateTime) >= new Date(endDateTime)) {
      setError("Start date-time must be before end date-time");
      return false;
    }
    
    if (!sensorId.trim()) {
      setError("Sensor ID is required");
      return false;
    }
    
    setError("");
    return true;
  }, [startDateTime, endDateTime, sensorId]);

  const generateFilename = useCallback((name: string, start: string, end: string, sensorId: string) => {
    const formatDateTime = (dateTime: string) => {
      return new Date(dateTime).toISOString().replace(/[:.]/g, '-').slice(0, -5);
    };
    
    return `${name}_${formatDateTime(start)}_${formatDateTime(end)}_${sensorId}`;
  }, []);


  const handleDownload = useCallback(async (dataType: "raw" | "processed") => {
    if (!validateForm()) return;

    setIsDownloading(prev => ({ ...prev, [dataType]: true }));

    try {
      // Get the current user's ID token from Firebase
      const token = await auth.currentUser?.getIdToken();
      
      if (!token) {
        throw new Error("No authentication token available");
      }
      
      const params = new URLSearchParams({
        userId: userId || "",
        sensorId: sensorId.trim(),
        start: new Date(startDateTime).toISOString(),
        end: new Date(endDateTime).toISOString(),
        exportType: dataType,
        format: exportFormat,
        auth_token: token  // Add the Firebase ID token as a query parameter
      });

      const url = `${process.env.REACT_APP_API_HOSTNAME}/export/sensor-data?${params.toString()}`;
      
      // Get filename for the download
      const filename = `${generateFilename(dataType, startDateTime, endDateTime, sensorId.trim())}.${exportFormat}`;
      
      // Open download in a new window/tab to prevent interference with other downloads
      window.open(url, '_blank');
      
      // Reset download state after a short delay
      setTimeout(() => {
        setIsDownloading(prev => ({ ...prev, [dataType]: false }));
      }, 2000);
    } catch (error: any) {
      console.error(`Failed to download ${dataType} data:`, error);
      setError(`Failed to download ${dataType} data: ${error.message || 'Unknown error'}`);
      setIsDownloading(prev => ({ ...prev, [dataType]: false }));
    }
  }, [validateForm, sensorId, userId, startDateTime, endDateTime, generateFilename, exportFormat]);

  const handleClose = useCallback(() => {
    setStartDateTime("");
    setEndDateTime("");
    setSensorId("");
    setError("");
    setIsDownloading({ raw: false, processed: false });
    setExportFormat("csv");
    onHide();
  }, [onHide]);

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton className="bg-dark text-light">
        <Modal.Title>Export Sensor Data</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light">
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        
        <Form>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Start Date-Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={startDateTime}
                  onChange={(e) => setStartDateTime(e.target.value)}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>End Date-Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={endDateTime}
                  onChange={(e) => setEndDateTime(e.target.value)}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Export Format</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    id="format-csv"
                    label="CSV"
                    name="exportFormat"
                    value="csv"
                    checked={exportFormat === "csv"}
                    onChange={(e) => setExportFormat(e.target.value as "csv" | "json")}
                    className="text-light"
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="format-json"
                    label="JSON"
                    name="exportFormat"
                    value="json"
                    checked={exportFormat === "json"}
                    onChange={(e) => setExportFormat(e.target.value as "csv" | "json")}
                    className="text-light"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Sensor ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter sensor ID"
              value={sensorId}
              onChange={(e) => setSensorId(e.target.value)}
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="bg-dark border-secondary">
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => handleDownload("raw")}
          disabled={isDownloading.raw || isDownloading.processed}
        >
          <FontAwesomeIcon icon={faDownload} className="me-1" />
          {isDownloading.raw ? "Downloading..." : "Download Raw Data"}
        </Button>
        <Button
          variant="success"
          onClick={() => handleDownload("processed")}
          disabled={isDownloading.raw || isDownloading.processed}
        >
          <FontAwesomeIcon icon={faDownload} className="me-1" />
          {isDownloading.processed ? "Downloading..." : "Download Processed Data"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}